/**
 * LinkPicker - универсальный компонент выбора ссылки
 *
 * Поддерживает два типа ссылок:
 * - Страница проекта (/ или /slug или /slug#section-X или #section-X для текущей)
 * - Внешняя ссылка (https://...)
 */
import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../AppIcon';

// Парсим ссылку на страницу и секцию
function parsePageLink(value) {
  if (!value) return { page: '', section: '' };

  // Если только хеш - это текущая страница
  if (value.startsWith('#')) {
    return { page: '__current__', section: value };
  }

  if (!value.startsWith('/')) return { page: '', section: '' };

  const hashIndex = value.indexOf('#');
  if (hashIndex === -1) {
    return { page: value, section: '' };
  }

  return {
    page: value.slice(0, hashIndex),
    section: value.slice(hashIndex)
  };
}

// Определяем тип ссылки по значению
function getLinkType(value) {
  if (!value) return 'none';
  if (value.startsWith('#')) return 'page'; // секция на текущей = тип page
  if (value.startsWith('/') || value === '/') return 'page';
  if (value.startsWith('http://') || value.startsWith('https://')) return 'external';
  return 'external';
}

// Хелпер для получения текста из строки или объекта
function getTextValue(value) {
  if (!value) return '';
  if (typeof value === 'object' && value !== null) {
    return value.text || '';
  }
  return value;
}

// Получить отображаемое имя секции
function getSectionLabel(section, index) {
  const rawTitle = section?.content?.title
    || section?.content?.sectionTitle
    || section?.content?.heading
    || section?.content?.formTitle;

  const title = getTextValue(rawTitle);

  if (title && title.trim()) {
    return title;
  }

  return section?.name || section?.type || `Секция ${index + 1}`;
}

export default function LinkPicker({
  value = '',
  onChange,
  pages = [],
  sections = [], // секции текущей страницы
  placeholder = 'Выберите ссылку',
  label,
  className = '',
  showLabel = true,
}) {
  const [linkType, setLinkType] = useState(() => getLinkType(value) || 'none');

  // Синхронизируем тип при изменении value извне
  useEffect(() => {
    const detectedType = getLinkType(value);
    if (detectedType !== 'none' && detectedType !== linkType) {
      setLinkType(detectedType);
    }
  }, [value]);

  // Парсим текущее значение
  const { page: currentPage, section: currentPageSection } = useMemo(() => {
    if (linkType === 'page') {
      return parsePageLink(value);
    }
    return { page: '', section: '' };
  }, [value, linkType]);

  // Получаем секции выбранной страницы
  const selectedPageSections = useMemo(() => {
    if (linkType !== 'page' || !currentPage) return [];

    // Если текущая страница
    if (currentPage === '__current__') {
      return sections;
    }

    // Находим страницу по slug
    const pageSlug = currentPage === '/' ? '' : currentPage.slice(1);
    const selectedPage = pages.find(p => {
      const pSlug = p.slug === '' || p.slug === 'index' ? '' : p.slug;
      return pSlug === pageSlug;
    });

    return selectedPage?.sections || [];
  }, [linkType, currentPage, pages, sections]);

  const handleTypeChange = (newType) => {
    setLinkType(newType);
    if (newType === 'none') {
      onChange('');
    } else if (newType === 'page') {
      // По умолчанию - текущая страница
      onChange('#section-0');
    } else if (newType === 'external') {
      onChange('https://');
    }
  };

  const handlePageChange = (e) => {
    const newPage = e.target.value;
    if (newPage === '__current__') {
      // Для текущей страницы - только якорь
      onChange(sections.length > 0 ? '#section-0' : '');
    } else {
      onChange(newPage);
    }
  };

  const handlePageSectionChange = (e) => {
    const newSection = e.target.value;
    if (currentPage === '__current__') {
      // Для текущей страницы - только якорь
      onChange(newSection || '');
    } else {
      onChange(currentPage + newSection);
    }
  };

  const handleExternalChange = (e) => {
    onChange(e.target.value);
  };

  const linkTypes = [
    { value: 'none', label: 'Нет', icon: 'X' },
    { value: 'page', label: 'Страница', icon: 'FileText' },
    { value: 'external', label: 'Внешняя', icon: 'ExternalLink' },
  ];

  return (
    <div className={`space-y-2 ${className}`}>
      {showLabel && label && (
        <label className="block text-sm font-medium">{label}</label>
      )}

      {/* Выбор типа ссылки */}
      <div className="flex gap-1 p-1 bg-muted rounded-lg">
        {linkTypes.map((type) => (
          <button
            key={type.value}
            onClick={() => handleTypeChange(type.value)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded text-xs font-medium transition-colors ${
              linkType === type.value
                ? 'bg-background shadow-sm text-foreground'
                : 'text-text-secondary hover:text-foreground'
            }`}
          >
            <Icon name={type.icon} size={14} />
            <span className="hidden sm:inline">{type.label}</span>
          </button>
        ))}
      </div>

      {/* Выбор страницы */}
      {linkType === 'page' && (
        <div className="space-y-2">
          <select
            value={currentPage}
            onChange={handlePageChange}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-base md:text-sm"
          >
            {/* Текущая страница */}
            <option value="__current__">Текущая страница</option>

            {/* Остальные страницы */}
            {pages.map((page) => {
              const isHome = page.slug === '' || page.slug === 'index' || page.order === 0;
              const pageValue = isHome ? '/' : `/${page.slug}`;
              return (
                <option key={page.id} value={pageValue}>
                  {isHome ? `${page.name} (Главная)` : page.name}
                </option>
              );
            })}
          </select>

          {/* Выбор секции на странице */}
          {selectedPageSections.length > 0 && (
            <select
              value={currentPageSection}
              onChange={handlePageSectionChange}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-base md:text-sm"
            >
              <option value="">Начало страницы</option>
              {selectedPageSections.map((section, index) => (
                <option key={section?.id || index} value={`#section-${index}`}>
                  ↳ {getSectionLabel(section, index)}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {linkType === 'external' && (
        <input
          type="text"
          value={value}
          onChange={handleExternalChange}
          placeholder="https://example.com"
          className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-base md:text-sm"
        />
      )}

      {/* Превью текущего значения */}
      {value && linkType !== 'none' && (
        <div className="flex items-center gap-2 px-2 py-1.5 bg-muted/50 rounded text-xs text-text-secondary">
          <Icon name={linkType === 'page' ? 'FileText' : 'ExternalLink'} size={12} />
          <span className="truncate">{value}</span>
        </div>
      )}
    </div>
  );
}
